import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { hapticTick } from '../../../_utils/haptics';

/**
 * The kit every chapter is built from, so the whole setup reads as one
 * piece. Chapters never style themselves: they compose these.
 */

/* ---------------- icons ---------------- */

export const Icon = {
  Tick: ({ size = 12, stroke = 3 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>),
  TickBig: () => (<svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>),
  Arrow: ({ size = 16 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>),
  Back: ({ size = 16 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>),
  Close: ({ size = 16 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>),
  Clock: ({ size = 14 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>),
  Shield: ({ size = 14 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l7 3v6c0 4.4-3 8.2-7 9-4-.8-7-4.6-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>),
  Info: ({ size = 16 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>),
  Warn: ({ size = 16 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>),
  Bin: ({ size = 16 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></svg>),
  Plus: ({ size = 16 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>),
  External: ({ size = 14 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>),
  Copy: ({ size = 14 }) => (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a1 1 0 0 1 1-1h10" /></svg>),
};

export const initials = (name = '') => String(name).split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';
export const money = (n, symbol = '£') => `${symbol}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 })}`;
export const reducedMotion = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } };

/* ---------------- Bailey's words, one at a time ---------------- */

export function Say({ text, instant, waiting = false }) {
  const words = useMemo(() => String(text || '').split(' ').filter(Boolean), [text]);
  const quick = instant || reducedMotion();
  return (
    <p className={`su-say${waiting ? ' is-waiting' : ''}`}>
      {words.map((w, i) => (
        <span key={`${i}-${w}`} className="su-word" style={quick ? undefined : { animationDelay: `${Math.min(i * 28, 1400)}ms` }}>{w}{' '}</span>
      ))}
    </p>
  );
}

/**
 * Bailey's speech bubble. It stays on screen and eases to the size of each
 * new line, rather than popping in again and jumping the page under it. A
 * line only shows once it has held for a moment, and after a change of
 * chapter it waits for the chapter to load (up to `holdUntil`), so the
 * chapter's opening line that its first question replaces straight away
 * never flashes past. While a line waits, the old one dims.
 */
const SETTLE_MS = 140;
export function BaileyBubble({ line, className, holdUntil = 0 }) {
  const [shown, setShown] = useState(line);
  useEffect(() => {
    if (line === shown) return undefined;
    const t = setTimeout(() => setShown(line), Math.max(SETTLE_MS, holdUntil - Date.now()));
    return () => clearTimeout(t);
  }, [line, shown, holdUntil]);
  const inner = useRef(null);
  const [height, setHeight] = useState(null);
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return undefined;
    const measure = () => setHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className={className} aria-hidden="true">
      <div className="su-bubble-fit" style={height === null ? undefined : { height }}>
        <div ref={inner}>
          <span className="su-bubble-name">Bailey</span>
          <Say key={shown} text={shown} waiting={line !== shown} />
        </div>
      </div>
    </div>
  );
}

/* ---------------- chapter ↔ shell ---------------- */

export const ChapterUI = createContext(null);
export const useChapterUI = () => useContext(ChapterUI);

/**
 * Tell the footer what the primary button does. Re-sent whenever the label,
 * state or handler changes; cleared when the chapter goes.
 *   useFooter({ label: 'Continue', onClick, disabled, busy, secondary: { label, onClick }, note })
 * Also: busyLabel, hideSkip, hidden, noArrow, and back: { onClick } to step
 * back through the chapter's own questions before leaving it.
 */
export function useFooter(spec, deps) {
  const ui = useChapterUI();
  const ref = useRef(spec);
  ref.current = spec;
  useEffect(() => {
    if (!ui) return undefined;
    ui.setFooter(spec ? {
      ...spec,
      onClick: spec.onClick ? (...a) => ref.current && ref.current.onClick && ref.current.onClick(...a) : undefined,
      secondary: spec.secondary ? { ...spec.secondary, onClick: (...a) => ref.current && ref.current.secondary && ref.current.secondary.onClick(...a) } : undefined,
      back: spec.back ? { onClick: (...a) => ref.current && ref.current.back && ref.current.back.onClick(...a) } : undefined,
    } : null);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => () => { if (ui) ui.setFooter(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * What Bailey says (and how he looks) while this part of a chapter is up.
 * No line (null) keeps the chapter's own opening line from setupPlan.js, so
 * a chapter's first screen can let Bailey introduce it.
 */
export function useBailey(line, mood = 'attentive', activity = 'none') {
  const ui = useChapterUI();
  useEffect(() => {
    if (ui) ui.say(line || null, mood, activity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line, mood, activity]);
}

/* ---------------- answering ---------------- */

export function Choices({ options, value, onChange, columns = 2, multiple = false, label, compact = false }) {
  const isOn = (v) => (multiple ? (value || []).includes(v) : value === v);
  const pick = (v) => {
    hapticTick();
    if (!multiple) { onChange(v); return; }
    const cur = value || [];
    onChange(cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };
  return (
    <div className={`su-choices${columns === 1 ? ' su-choices--1' : columns === 3 ? ' su-choices--3' : ''}${compact ? ' su-choices--compact' : ''}`} role={multiple ? 'group' : 'radiogroup'} aria-label={label}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role={multiple ? 'checkbox' : 'radio'}
          aria-checked={isOn(o.value)}
          className={`su-choice${isOn(o.value) ? ' is-on' : ''}`}
          onClick={() => pick(o.value)}
        >
          <b>{o.label}</b>
          {o.hint && <span>{o.hint}</span>}
          <i className="su-choice-tick" aria-hidden="true"><Icon.Tick size={11} /></i>
        </button>
      ))}
    </div>
  );
}

export function Chips({ options, value, onChange, label }) {
  const cur = value || [];
  const toggle = (v) => { hapticTick(); onChange(cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]); };
  return (
    <div className="su-chips" role="group" aria-label={label}>
      {options.map((o) => {
        const on = cur.includes(o.value);
        return (
          <button key={o.value} type="button" aria-pressed={on} className={`su-chip${on ? ' is-on' : ''}`} onClick={() => toggle(o.value)}>
            {on && <Icon.Tick size={11} />}{o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Stepper({ value, onChange, min = 0, max = 50, label }) {
  const v = Number(value) || 0;
  return (
    <span className="su-stepper" role="group" aria-label={label}>
      <button type="button" onClick={() => { hapticTick(); onChange(Math.max(min, v - 1)); }} disabled={v <= min} aria-label={`Fewer ${label || ''}`}>−</button>
      <b aria-live="polite">{v}</b>
      <button type="button" onClick={() => { hapticTick(); onChange(Math.min(max, v + 1)); }} disabled={v >= max} aria-label={`More ${label || ''}`}>+</button>
    </span>
  );
}

export function Toggle({ on, onChange, label, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} disabled={disabled} className={`su-toggle${on ? ' is-on' : ''}`} onClick={() => { hapticTick(); onChange(!on); }} />
  );
}

/* ---------------- layout ---------------- */

export function Card({ title, sub, action, done, quiet, children, className = '' }) {
  return (
    <section className={`su-card${done ? ' su-card--done' : ''}${quiet ? ' su-card--quiet' : ''} ${className}`}>
      {(title || action) && (
        <header className="su-card-head">
          <div style={{ minWidth: 0 }}>
            {title && <h3 className="su-card-title">{title}</h3>}
            {sub && <p className="su-card-sub">{sub}</p>}
          </div>
          {action && <div className="su-card-action">{action}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Row({ avatar, title, meta, children }) {
  return (
    <div className="su-row">
      {avatar}
      <div className="su-row-main"><b>{title}</b>{meta && <span>{meta}</span>}</div>
      {children}
    </div>
  );
}

export function Avatar({ name, src, tone }) {
  return <span className={`su-avatar${tone ? ` su-avatar--${tone}` : ''}`} aria-hidden="true">{src ? <img src={src} alt="" /> : initials(name)}</span>;
}

export function Note({ tone, children, icon }) {
  const I = icon || (tone === 'warn' ? Icon.Warn : Icon.Info);
  return <div className={`su-note${tone ? ` su-note--${tone}` : ''}`}><I size={16} /><div>{children}</div></div>;
}

export function Status({ tone = 'ok', children }) {
  return <span className={`su-status su-status--${tone}`}>{children}</span>;
}

export function Saved({ children = 'Saved' }) {
  return <span className="su-saved"><Icon.Tick size={11} />{children}</span>;
}

export const Working = ({ children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span className="su-dots" aria-hidden="true"><i /><i /><i /></span>{children}</span>
);

export function Skeleton({ h = 18, w = '100%', style }) {
  return <span className="su-skel" style={{ height: h, width: w, ...style }} aria-hidden="true" />;
}

export function Field({ label, children, hint }) {
  return (
    <label className="su-field">
      <span>{label}</span>
      {children}
      {hint && <small style={{ fontSize: 12.5, color: 'var(--su-muted)' }}>{hint}</small>}
    </label>
  );
}

/** A finished moment: the tick, a line, and onwards. */
export function Done({ title, children }) {
  return (
    <div className="su-done">
      <div className="su-done-mark"><Icon.TickBig /></div>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}

/* ---------------- a burst of sparks, where something lands ---------------- */

export function sparkAt(x, y) {
  if (reducedMotion() || typeof document === 'undefined') return;
  let layer = document.querySelector('.su-fx');
  if (!layer) { layer = document.createElement('div'); layer.className = 'su-fx'; document.body.appendChild(layer); }
  const nodes = [];
  for (let i = 0; i < 9; i += 1) {
    const a = (i / 9) * Math.PI * 2 + Math.random() * 0.4;
    const r = 34 + Math.random() * 30;
    const n = document.createElement('span');
    n.className = `su-spark${i % 3 === 1 ? ' su-spark--gold' : i % 3 === 2 ? ' su-spark--violet' : ''}`;
    n.style.left = `${x}px`;
    n.style.top = `${y}px`;
    n.style.setProperty('--dx', `${Math.cos(a) * r}px`);
    n.style.setProperty('--dy', `${Math.sin(a) * r}px`);
    n.style.setProperty('--rot', `${Math.round(Math.random() * 180)}deg`);
    n.style.setProperty('--d', `${Math.round(Math.random() * 90)}ms`);
    layer.appendChild(n);
    nodes.push(n);
  }
  setTimeout(() => nodes.forEach((n) => n.remove()), 1200);
}

export function sparkFrom(el) {
  if (!el) return;
  const r = el.getBoundingClientRect();
  sparkAt(r.left + r.width / 2, r.top + r.height / 2);
}

/* ---------------- a time of day, as a dropdown ---------------- */

// Every 15 minutes from 5am to 11pm. Matches the duration dropdowns rather
// than the browser's own time box (Ben, 6 Oct 2026). A saved time that isn't
// on the list (09:10) is kept as an option, so nothing changes by itself.
const CLOCK_OPTIONS = (() => { const out = []; for (let m = 5 * 60; m <= 23 * 60; m += 15) out.push(m); return out; })();
const two = (n) => String(n).padStart(2, '0');
const hhmm = (m) => `${two(Math.floor(m / 60))}:${two(m % 60)}`;
const minsOf = (v) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

export function ClockSelect({ value, onChange, label, disabled, className = '' }) {
  const at = minsOf(value);
  const options = at === null || CLOCK_OPTIONS.includes(at) ? CLOCK_OPTIONS : CLOCK_OPTIONS.concat(at).sort((a, b) => a - b);
  return (
    <select
      className={`su-select su-clock${className ? ` ${className}` : ''}`}
      value={at === null ? '' : hhmm(at)}
      aria-label={label}
      disabled={disabled}
      onChange={(e) => { const v = e.target.value; onChange(v); }}
    >
      {at === null && <option value="" disabled>Time</option>}
      {options.map((m) => <option key={m} value={hhmm(m)}>{hhmm(m)}</option>)}
    </select>
  );
}
